import { useTranslation } from 'react-i18next';
import { useApi } from '../../../app/providers';
import type { Board, Folder, Project } from '../../../lib/api';
import { useMoveBoards } from '../BoardActions';
import { useFolders, useInvalidateContent, useOrg, useProjects } from '../queries';
import { useSelection } from '../SelectionScope';
import { reorder } from './drop-target';
import { useDrag } from './useDrag';
import { useNotify } from '../undo';

/** Native drag of the card's link and image would cancel the pointer drag. */
const noNativeDrag = (e: { preventDefault: () => void }) => e.preventDefault();

/** A board card drags itself, or the whole selection when it is part of it, onto a project or a folder. */
export function useBoardDrag(board: Board) {
  const { t } = useTranslation();
  const { org } = useOrg();
  const scope = useSelection();
  const move = useMoveBoards();
  const notify = useNotify();
  const drag = useDrag({
    payload: () => {
      if (!org || org.id !== board.orgId) return null;
      const selected = scope?.selection.ids.has(board.id) ? scope.boards().filter((b) => scope.selection.ids.has(b.id)) : [board];
      return { kind: 'boards', boards: selected };
    },
    label: (p) => (p.kind === 'boards' && p.boards.length > 1 ? t('dashboard.dnd.boards', { count: p.boards.length }) : board.title),
    onDrop: (p, target) => {
      if (p.kind !== 'boards') return;
      // Refused (the folder went meanwhile, rights changed): say so; nothing moved, nothing to undo.
      void move(p.boards as Board[], { projectId: target.projectId, folderId: target.folderId ?? null }).catch(() => notify(t('dashboard.bulk.failed')));
    },
  });
  return { onPointerDown: drag.onPointerDown, onDragStart: noNativeDrag };
}

/** Project cards reorder by drag (members only). */
export function useProjectDrag(project: Project) {
  const { t } = useTranslation();
  const api = useApi();
  const notify = useNotify();
  const { org } = useOrg();
  const projects = useProjects(org?.id);
  const invalidate = useInvalidateContent();
  const drag = useDrag({
    payload: () => (org && org.id === project.orgId ? { kind: 'project', projectId: project.id } : null),
    label: () => project.name,
    onDrop: (_p, target) => {
      const keys = reorder(projects.data ?? [], project.id, target.projectId);
      if (keys.size === 0 || !org) return;
      void api
        .reorderProjects(org.id, [...keys].map(([id, position]) => ({ id, position })))
        .catch(() => notify(t('dashboard.bulk.failed')))
        .finally(() => void invalidate());
    },
  });
  return { onPointerDown: drag.onPointerDown, onDragStart: noNativeDrag };
}

/** Folder sections reorder by dragging their heading. */
export function useFolderDrag(folder: Folder, writable: boolean) {
  const { t } = useTranslation();
  const api = useApi();
  const notify = useNotify();
  const folders = useFolders(folder.projectId);
  const invalidate = useInvalidateContent();
  const drag = useDrag({
    payload: () => (writable ? { kind: 'folder', projectId: folder.projectId, folderId: folder.id } : null),
    label: () => folder.name,
    onDrop: (_p, target) => {
      const keys = reorder(folders.data ?? [], folder.id, target.folderId!);
      void Promise.all([...keys].map(([id, position]) => api.setFolderPosition(id, position)))
        .catch(() => notify(t('dashboard.bulk.failed')))
        .finally(() => void invalidate());
    },
  });
  return { onPointerDown: drag.onPointerDown, onDragStart: noNativeDrag };
}
